import React, { useState } from 'react'
import {
  buildAfter,
  clearFormDraft,
  editableFields,
  readFormDraft,
  writeFormDraft,
} from './blockChangeHelpers'

const CUSTOMER_TYPE_OPTIONS = ['ALL', 'NEW', 'REACT']

const FIELD_LABELS = {
  name: 'Name',
  attribute: 'Attribute',
  customer_type: 'Customer type',
  treatment_id: 'Treatment id',
  block_action: 'Action',
  min: 'Trips below (min)',
  max: 'Trips above (max)',
  rejection_reason: 'Rejection reason',
  adverse_action_score_key: 'Adverse action score key',
  pre_uw: 'pre_uw',
  post_buy: 'post_buy',
  is_compliance: 'is_compliance',
  type: 'type',
  conditions: 'Conditions (all must match)',
}

const SELECT_OPTIONS = {
  block_action: (vocabulary) =>
    (vocabulary?.block_actions ?? []).map((action) => [
      String(action.value),
      action.label,
    ]),
  customer_type: () => CUSTOMER_TYPE_OPTIONS.map((type) => [type, type]),
}

function asInput(value) {
  return value === null || value === undefined ? '' : String(value)
}

function ConditionsEditor({ conditions, fieldNames, onChange }) {
  const update = (index, patch) => {
    const next = [...conditions]
    next[index] = { ...next[index], ...patch }
    onChange(next)
  }
  const remove = (index) => onChange(conditions.filter((_, i) => i !== index))
  const add = () =>
    onChange([...conditions, { field_name: '', field_value: '' }])
  const setName = (index) => (event) => {
    update(index, { field_name: event.target.value })
  }
  const setValue = (index) => (event) => {
    update(index, { field_value: event.target.value })
  }

  return (
    <div data-testid="block-field-conditions">
      {conditions.map((condition, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className="field has-addons mb-1" key={index}>
          <div className="control is-expanded">
            <input
              className="input is-small"
              placeholder="field name"
              list="block-field-names"
              value={condition.field_name}
              onChange={setName(index)}
            />
          </div>
          <div className="control is-expanded">
            <input
              className="input is-small"
              placeholder="value"
              value={condition.field_value}
              onChange={setValue(index)}
            />
          </div>
          <div className="control">
            <button
              type="button"
              className="button is-small"
              aria-label="Remove condition"
              onClick={() => remove(index)}
            >
              ×
            </button>
          </div>
        </div>
      ))}
      <datalist id="block-field-names">
        {fieldNames.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
      <button type="button" className="button is-small is-text" onClick={add}>
        + Add condition
      </button>
    </div>
  )
}

// Unsent input is kept in localStorage per request, row and op; the saved draft on the server is the truth.
export default function BlockEditForm({
  op,
  table,
  row,
  currentConditions,
  initialValues,
  vocabulary,
  onRetryVocabulary = null,
  draftKey,
  excludeFields = [],
  createdByOp = null,
  isBusy,
  onSave,
  onCancel,
}) {
  const fields = editableFields({ op, table, createdByOp }).filter(
    (field) => !excludeFields.includes(field)
  )
  const [values, setValues] = useState(
    () => readFormDraft(draftKey, initialValues) ?? initialValues
  )
  const [errors, setErrors] = useState({})
  const [notice, setNotice] = useState(null)

  const setField = (field, value) => {
    const next = { ...values, [field]: value }
    setValues(next)
    setNotice(null)
    writeFormDraft({ ...draftKey, values: next, initialValues })
  }

  const save = () => {
    const built = buildAfter({
      op,
      table,
      row,
      values,
      currentConditions,
      createdByOp,
    })
    setErrors(built.errors)
    if (Object.keys(built.errors).length > 0) return
    if (Object.keys(built.after).length === 0) {
      setNotice('Nothing has changed.')
      return
    }
    onSave(built.after, setErrors).then((saved) => {
      if (!saved) return
      clearFormDraft(draftKey)
      setNotice(null)
    })
  }

  const cancel = () => {
    clearFormDraft(draftKey)
    onCancel()
  }

  const renderInput = (field) => {
    if (field === 'conditions') {
      return (
        <ConditionsEditor
          conditions={values.conditions ?? []}
          fieldNames={vocabulary?.field_names ?? []}
          onChange={(conditions) => setField('conditions', conditions)}
        />
      )
    }
    const options = SELECT_OPTIONS[field]?.(vocabulary) ?? null
    if (options) {
      return (
        <div className="select is-small is-fullwidth">
          <select
            value={asInput(values[field])}
            onChange={(event) => setField(field, event.target.value)}
            data-testid={`block-field-${field}`}
          >
            <option value="">—</option>
            {options.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      )
    }
    return (
      <input
        className="input is-small"
        list={field === 'attribute' ? 'block-attributes' : undefined}
        value={asInput(values[field])}
        onChange={(event) => setField(field, event.target.value)}
        data-testid={`block-field-${field}`}
      />
    )
  }

  return (
    <div className="lox-block-change__form mt-3" data-testid="block-edit-form">
      {fields.map((field) => (
        <div className="field mb-2" key={field}>
          <label className="label is-small mb-0">
            {FIELD_LABELS[field] ?? field}
          </label>
          {renderInput(field)}
          {field === 'block_action' && onRetryVocabulary && (
            <p className="help is-danger" data-testid="block-vocabulary-error">
              The actions could not be loaded.{' '}
              <button
                type="button"
                className="button is-small is-text p-0"
                onClick={onRetryVocabulary}
                data-testid="block-vocabulary-retry"
              >
                Retry
              </button>
            </p>
          )}
          {errors[field] && <p className="help is-danger">{errors[field]}</p>}
        </div>
      ))}
      <datalist id="block-attributes">
        {(vocabulary?.attributes ?? []).map((attribute) => (
          <option key={attribute} value={attribute} />
        ))}
      </datalist>
      {notice && (
        <p className="help" data-testid="block-edit-notice">
          {notice}
        </p>
      )}
      <div className="buttons mt-2">
        <button
          type="button"
          className="button is-small is-primary"
          disabled={isBusy}
          onClick={save}
          data-testid="block-edit-save"
        >
          Add to draft
        </button>
        <button type="button" className="button is-small" onClick={cancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}
